/**
 * Story 042 — Image-state filter store. Drives the "show inactive
 * images" toggle from the Settings drawer (Story 036) and the
 * cross-widget filter cascade.
 *
 * Default state: `['active']` — inactive data points / defects / images
 * are hidden until the user opts in.
 *
 * Persistence: the Settings drawer reads/writes the underlying value
 * via Story 034's preferences hook. This store is the in-memory
 * authority for the current page session.
 */
import { create } from "zustand";

export type ImageState = "active" | "inactive";

export interface ImageStateFilterStore {
  states: readonly ImageState[];
  setStates: (next: readonly ImageState[]) => void;
  /**
   * Pure filter helper — returns the items whose `dataPoint.isActive`
   * matches the current state set. Items without a `dataPoint` shape
   * are treated as active (don't filter what we can't classify).
   */
  applyTo: <T extends { dataPoint?: { isActive?: boolean } | null }>(
    items: readonly T[],
  ) => readonly T[];
}

const DEFAULT_STATES: readonly ImageState[] = ["active"];

export const useImageStateFilterStore = create<ImageStateFilterStore>(
  (set, get) => ({
    states: DEFAULT_STATES,
    setStates: (next) => {
      // Normalize ordering and dedupe so peers comparing references can
      // skip re-renders when the underlying set is unchanged.
      const filtered = (["active", "inactive"] as const).filter((s) =>
        next.includes(s),
      );
      // Guarantee at least one — empty set would hide everything.
      const final = filtered.length === 0 ? DEFAULT_STATES : filtered;
      set({ states: final });
    },
    applyTo: (items) => {
      const states = get().states;
      const includeActive = states.includes("active");
      const includeInactive = states.includes("inactive");
      if (includeActive && includeInactive) return items;
      return items.filter((item) => {
        const isActive = item.dataPoint?.isActive;
        if (isActive === false) return includeInactive;
        // null / undefined / true — treat as active.
        return includeActive;
      });
    },
  }),
);

/**
 * Test-bridge exposure under `window.__aeroImageStateFilter`. The
 * acceptance spec drives `setStates` and asserts `applyTo` returns the
 * expected subset.
 */
export interface ImageStateFilterTestBridge {
  states: readonly ImageState[];
  setStates: (next: readonly ImageState[]) => void;
  applyTo: <T extends { dataPoint?: { isActive?: boolean } | null }>(
    items: readonly T[],
  ) => readonly T[];
}

declare global {
  interface Window {
    __aeroImageStateFilter?: ImageStateFilterTestBridge;
  }
}

export function mountImageStateFilterBridge(): () => void {
  if (typeof window === "undefined") return () => undefined;
  const w = window;
  const get = (): ImageStateFilterTestBridge => {
    const s = useImageStateFilterStore.getState();
    return {
      states: s.states,
      setStates: s.setStates,
      applyTo: s.applyTo,
    };
  };
  Object.defineProperty(w, "__aeroImageStateFilter", {
    configurable: true,
    get,
  });
  return () => {
    delete w.__aeroImageStateFilter;
  };
}
